/*
 * EmailPreferencesCard
 *
 * A small, restful Settings card that lets users opt in / out of the Weekly
 * Reflection Digest and (for the curious) send themselves a preview
 * immediately so they can see what the email looks like.
 *
 * Design language matches AmbientCaptureSettings and the rest of the
 * sanctuary aesthetic: glass card, rose-gold accents, serif headers,
 * lucide-react icons, non-gamified copy.
 */

import React, { useEffect, useState } from 'react';
import { Card } from './ui/card';
import { Switch } from './ui/switch';
import { Button } from './ui/button';
import { Mail, Send, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';

const EmailPreferencesCard = () => {
  const { user, refreshUser } = useAuth() || {};
  // Optimistic mirror of server state; hydrated from the auth context.
  const [weeklyDigest, setWeeklyDigest] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingPreview, setSendingPreview] = useState(false);
  const [previewSent, setPreviewSent] = useState(false);

  // Hydrate from the current user (auth context is loaded on mount).
  useEffect(() => {
    const prefs = user?.email_preferences;
    if (prefs && typeof prefs.weekly_digest === 'boolean') {
      setWeeklyDigest(prefs.weekly_digest);
    }
  }, [user]);

  const handleToggle = async (nextValue) => {
    // Optimistic update
    setWeeklyDigest(nextValue);
    setSaving(true);
    try {
      await api.updateEmailPreferences({ weekly_digest: nextValue });
      if (typeof refreshUser === 'function') {
        // Best-effort silent refresh so the auth context stays fresh.
        try {
          await refreshUser();
        } catch (_) {
          /* ignore */
        }
      }
      toast.success(
        nextValue
          ? 'Weekly reflection turned on'
          : 'Weekly reflection turned off',
        {
          description: nextValue
            ? "We'll send a quiet look at your week every Saturday."
            : "You won't receive the weekly reflection email. You can turn it back on any time.",
        },
      );
    } catch (err) {
      // Roll back optimistic state
      setWeeklyDigest(!nextValue);
      toast.error('Could not save that preference', {
        description: err?.message || 'Please try again in a moment.',
      });
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    setSendingPreview(true);
    setPreviewSent(false);
    try {
      const result = await api.sendWeeklyDigestPreview();
      setPreviewSent(true);
      toast.success('Preview sent', {
        description: `Check ${result?.to || 'your inbox'} in a moment.`,
      });
      // Reset the "sent" indicator after a few seconds so users can send again.
      setTimeout(() => setPreviewSent(false), 4500);
    } catch (err) {
      toast.error('Could not send preview', {
        description: err?.message || 'Please try again in a moment.',
      });
    } finally {
      setSendingPreview(false);
    }
  };

  return (
    <Card
      className="glass-card p-6 sm:p-8"
      id="email-preferences"
      data-testid="email-preferences-settings"
    >
      <div className="flex items-start gap-3">
        <Mail
          size={22}
          className="text-[var(--sd-primary)] mt-0.5 flex-shrink-0"
        />
        <div>
          <h2
            className="text-[20px] sm:text-[22px] font-semibold"
            style={{
              color: 'var(--sd-fg)',
              fontFamily: '"Playfair Display", serif',
              letterSpacing: '-0.01em',
            }}
          >
            Reflection Emails
          </h2>
          <p
            className="text-[13.5px] leading-relaxed mt-1.5 max-w-2xl"
            style={{ color: 'var(--sd-muted-fg)' }}
          >
            A quiet weekly email that looks back on your last seven days —
            themes, categories, gentle reminders. No streaks, no scoreboards.
          </p>
        </div>
      </div>

      {/* Weekly reflection toggle */}
      <div
        className="mt-6 rounded-xl p-4 sm:p-5 flex items-center justify-between gap-4"
        style={{
          background: 'rgba(245,245,245,0.05)',
          border: '1px solid rgba(245,245,245,0.12)',
        }}
      >
        <div className="min-w-0">
          <p
            className="text-[14.5px] font-semibold"
            style={{ color: 'var(--sd-fg)' }}
          >
            Weekly reflection
          </p>
          <p
            className="text-[12.5px] mt-1 leading-relaxed"
            style={{ color: 'var(--sd-muted-fg)' }}
          >
            Sent every Saturday. If your week was quiet, we send a gentle
            note; if it was full, we surface the themes that visited most.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {saving && (
            <Loader2
              size={14}
              className="animate-spin"
              style={{ color: 'var(--sd-muted-fg)' }}
              aria-hidden
            />
          )}
          <Switch
            checked={weeklyDigest}
            onCheckedChange={handleToggle}
            disabled={saving}
            data-testid="email-preferences-weekly-digest-toggle"
            aria-label="Toggle weekly reflection email"
          />
        </div>
      </div>

      {/* Preview button (only when opt-in) */}
      {weeklyDigest && (
        <div className="mt-3 flex flex-col sm:flex-row items-start sm:items-center gap-3 justify-between">
          <p
            className="text-[12px]"
            style={{ color: 'var(--sd-muted-fg)' }}
          >
            Curious what it looks like? Send yourself a preview now.
          </p>
          <Button
            type="button"
            onClick={handlePreview}
            disabled={sendingPreview}
            variant="secondary"
            className="text-[12.5px] whitespace-nowrap"
            data-testid="email-preferences-send-preview-button"
            style={{
              background: 'rgba(245,245,245,0.06)',
              color: 'var(--sd-fg)',
              border: '1px solid rgba(245,245,245,0.14)',
            }}
          >
            {sendingPreview ? (
              <>
                <Loader2 size={13} className="mr-2 animate-spin" />
                Sending…
              </>
            ) : previewSent ? (
              <>
                <Check size={13} className="mr-2" />
                Preview sent
              </>
            ) : (
              <>
                <Send size={13} className="mr-2" />
                Send me a preview
              </>
            )}
          </Button>
        </div>
      )}
    </Card>
  );
};

export default EmailPreferencesCard;
